"use client"

import type React from "react"
import { useState } from "react"
import type { SyncOption, SetupData } from "../SetupFlow"
import "./ChooseSync.css"

interface ChooseSyncProps {
  onSubmit: (options: SyncOption[]) => void
  onBack: () => void
  setupData: SetupData
}

const ChooseSync: React.FC<ChooseSyncProps> = ({ onSubmit, onBack, setupData }) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    setupData.selectedSyncOptions.filter((opt) => opt.selected).map((opt) => opt.id),
  )

  const syncOptions: SyncOption[] = [
    {
      id: "contacts-leads",
      title: "HubSpot Contact ↔ Salesforce Lead",
      description:
        "Sync, manage, and add HubSpot Contacts as Salesforce Leads automatically to keep your sales data up-to-date across both platforms.",
      selected: selectedOptions.includes("contacts-leads"),
    },
    {
      id: "tasks",
      title: "HubSpot Tasks → Salesforce Tasks",
      description:
        "Sync and manage tasks between HubSpot and Salesforce to keep your activities aligned and up-to-date. Keep track of to-dos, follow-ups, and activity logs across platforms.",
      selected: selectedOptions.includes("tasks"),
    },
    {
      id: "tickets-cases",
      title: "HubSpot Tickets → Salesforce Cases",
      description:
        "Sync and manage HubSpot Tickets as Salesforce Cases to streamline your customer support across both platforms to maintain a unified view of issue resolution across Salesforce and HubSpot.",
      selected: selectedOptions.includes("tickets-cases"),
    },
  ]

  const handleOptionToggle = (optionId: string) => {
    setSelectedOptions((prev) => (prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]))
  }

  const handleSubmit = () => {
    if (selectedOptions.length > 0) {
      const updatedOptions = syncOptions.map((option) => ({
        ...option,
        selected: selectedOptions.includes(option.id),
      }))
      onSubmit(updatedOptions)
    }
  }

  const renderDirectionArrow = (title: string) => {
    if (title.includes("↔")) {
      return (
        <div className="direction-arrow bidirectional">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 11l4 4-4 4" />
            <path d="M7 13l-4-4 4-4" />
            <path d="M21 15H3" />
            <path d="M21 9H3" />
          </svg>
        </div>
      )
    }

    return (
      <div className="direction-arrow unidirectional">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      </div>
    )
  }

  return (
    <div className="choose-sync">
      <div className="choose-sync-header">
        <h1>Choose What to Sync</h1>
        <p>Select the data you'd like to sync between HubSpot and Salesforce.</p>
        <p>You can always update this later.</p>
      </div>

      <div className="sync-options">
        {syncOptions.map((option) => (
          <div
            key={option.id}
            className={`sync-option-card ${selectedOptions.includes(option.id) ? "selected" : ""}`}
            onClick={() => handleOptionToggle(option.id)}
          >
            <div className="option-checkbox">
              <input
                type="checkbox"
                checked={selectedOptions.includes(option.id)}
                onChange={() => handleOptionToggle(option.id)}
              />
            </div>

            <div className="option-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>

            <div className="option-content">
              <div className="option-title">
                <span>
                  {option.title.split(" ")[0]} {option.title.split(" ")[1]}
                </span>
                {renderDirectionArrow(option.title)}
                <span>{option.title.split(" ").slice(-2).join(" ")}</span>
              </div>
              <div className="option-description">{option.description}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="settings-note">
        <p>You can update this later in Settings</p>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={onBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleSubmit} disabled={selectedOptions.length === 0}>
          Continue
        </button>
      </div>
    </div>
  )
}

export default ChooseSync
